import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  signal,
} from "@angular/core";
import { TournamentService } from "../../providers/tournament.service";
import { ClubService } from "../../providers/club.service";
import { BountyDraw } from "../../providers/clock-overlay.service";
import { TranslateModule } from "@ngx-translate/core";

// Presentational mystery-bounty reveal. Hosts the air-mix lottery-ball draw
// (Three.js + Rapier), lazy-loaded so the heavy 3D libs only download when a
// reveal actually plays. The same component renders on the fullscreen clock
// (overlay slot) and in the initiating device's dialog; both feed it the shared
// clockOverlay node `draws` so the timeline + values stay in sync (the engine
// anchors every phase to tournServ.clockNow()). One ceremony can play several
// draws back-to-back; the engine reports the CURRENT draw via onDrawState so the
// panel updates per draw. Emits `done` at the engine's absolute dismiss time.
@Component({
  selector: "app-bounty-reveal",
  templateUrl: "./bounty-reveal.component.html",
  styleUrls: ["./bounty-reveal.component.scss"],
  imports: [TranslateModule],
})
export class BountyRevealComponent implements AfterViewInit, OnDestroy {
  @Input() draws: BountyDraw[] = [];
  @Input() order: number[] = [];
  @Input() set atTs(v: number) {
    this._atTs = v || 0;
    this.maybePlay();
  }
  private _atTs = 0;

  @Output() done = new EventEmitter<void>();

  @ViewChild("host", { static: true }) hostRef!: ElementRef<HTMLElement>;

  // The currently displayed draw (updated per draw by the engine).
  readonly curKillerID = signal<string | null>(null);
  readonly curPlayerID = signal<string | null>(null);
  readonly curValue = signal<number | null>(null);
  // revealed → show the "winner" caption; failed → 3D unavailable, show fallback.
  readonly revealed = signal(false);
  readonly failed = signal(false);
  // Running list of awarded bounties (desktop multi-draw): {killerID, value}.
  readonly awarded = signal<
    { killerID: string | null; value: number | null }[]
  >([]);

  private engine: any = null;
  private played = false;
  private doneEmitted = false;
  private destroyed = false;
  private fallbackTimer: any = null;

  constructor(
    public tournServ: TournamentService,
    public clubServ: ClubService
  ) {}

  async ngAfterViewInit() {
    try {
      const mod = await import("./lottery/lottery-reveal");
      if (this.destroyed) return;
      this.engine = new mod.LotteryReveal(this.hostRef.nativeElement, {
        clockNow: () => this.now(),
        onDrawState: (s: any) => {
          this.curKillerID.set(s.draw?.killerID ?? null);
          this.curPlayerID.set(s.draw?.playerID ?? null);
          this.curValue.set(s.draw?.value ?? null);
          this.revealed.set(false); // new draw → hide the winner line until reveal
        },
        onRevealed: () => {
          this.revealed.set(true);
          // Append this draw to the awarded list (desktop sequence column).
          this.awarded.update((list) => [
            ...list,
            { killerID: this.curKillerID(), value: this.curValue() },
          ]);
        },
        onDismissed: () => this.emitDone(),
      });
      // Safety net: never let the overlay hang. Scale with the number of draws.
      this.fallbackTimer = setTimeout(
        () => this.emitDone(),
        mod.LotteryReveal.totalDurationMs(this.draws?.length || 1) + 5000
      );
      await this.engine.init();
      if (this.destroyed) {
        this.engine.dispose();
        return;
      }
      this.maybePlay();
    } catch (err) {
      // WebGL unavailable / chunk failed → degrade to a plain value reveal and
      // still dismiss on time.
      console.error("bounty 3D reveal failed to load", err);
      this.failed.set(true);
      const d0 = this.draws && this.draws[0];
      if (d0) {
        this.curKillerID.set(d0.killerID);
        this.curPlayerID.set(d0.playerID);
        this.curValue.set(d0.value);
      }
      this.revealed.set(true);
      this.fallbackTimer = setTimeout(() => this.emitDone(), 6000);
    }
  }

  private maybePlay() {
    if (this.engine && !this.played && this._atTs) {
      this.played = true;
      // Show the first draw's avatars immediately, through the load/blower phase
      // (before its trap opens) — otherwise the panel is blank at the start.
      const d0 = this.draws && this.draws[0];
      if (d0) {
        this.curKillerID.set(d0.killerID);
        this.curPlayerID.set(d0.playerID);
        this.curValue.set(d0.value);
      }
      this.engine.play({
        order: this.order || [],
        draws: this.draws || [],
        anchorMs: this._atTs,
      });
    }
  }

  private now(): number {
    try {
      return this.tournServ.clockNow();
    } catch {
      return Date.now();
    }
  }

  private emitDone() {
    if (this.doneEmitted) return;
    this.doneEmitted = true;
    this.done.emit();
  }

  // Multiple draws → show the awarded-list column on desktop.
  get isSequence(): boolean {
    return (this.draws?.length || 0) > 1;
  }

  // Undrawn prize values for the strip along the bottom, biggest first. The
  // server pool already excludes EVERY draw in this batch, so add back the ones
  // the audience hasn't seen revealed yet — each ball drops out of the strip at
  // its on-screen reveal, not when the server committed it (no spoilers).
  get remainingValues(): number[] {
    const pending = (this.draws || [])
      .slice(this.awarded().length)
      .map((d) => d?.value || 0)
      .filter((v) => v > 0);
    return [...this.tournServ.mysteryRemainingPrizes, ...pending].sort(
      (a, b) => b - a
    );
  }

  nameFor(killerID: string | null): string {
    const k = killerID && this.tournServ.latestTourn?.players?.[killerID];
    return k?.dispName || "";
  }

  imageFor(killerID: string | null): string {
    const k = killerID && this.tournServ.latestTourn?.players?.[killerID];
    return this.clubServ.getMemberImage((k && (k.baseuid || k.uid)) || killerID || "");
  }

  get killerName(): string {
    const id = this.curKillerID();
    const k = id && this.tournServ.latestTourn?.players?.[id];
    return k?.dispName || "";
  }

  get eliminatedName(): string {
    const id = this.curPlayerID();
    const p = id && this.tournServ.latestTourn?.players?.[id];
    return p?.dispName || "";
  }

  get killerImage(): string {
    const id = this.curKillerID();
    const k = id && this.tournServ.latestTourn?.players?.[id];
    return this.clubServ.getMemberImage((k && (k.baseuid || k.uid)) || id || "");
  }

  get eliminatedImage(): string {
    const id = this.curPlayerID();
    const p = id && this.tournServ.latestTourn?.players?.[id];
    return this.clubServ.getMemberImage((p && (p.baseuid || p.uid)) || id || "");
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    try {
      this.engine?.dispose();
    } catch {}
  }
}
